import React, { useState } from "react";
import { useIntl } from "react-intl";
import ChartSettingsDialog from "../ChartSettingsDialog";
import ChartTabs from "../ChartTabs";
import InputsTab from "../IndicatorSettingsModal/InputsTab";
import StylesTab from "../IndicatorSettingsModal/StylesTab";
import { drawingToolLabelId } from "../DrawingsMenu/drawingToolLabels";
import useDrawingDraft from "../../hooks/useDrawingDraft";
import { drawingSettingsOf, rowChangeToDraft, rowsToPlotStyles } from "./drawingSettingsRows";

const TAB_INPUTS = "inputs";
const TAB_STYLES = "styles";
const NO_ROWS = [];
const NO_LEVELS = [];

/**
 * Settings of one drawing (po-chart-visual-spec.md §7.5): the rows of its tool
 * (drawingSettingsRows.js) as in the Styles tab of an indicator, and with
 * inputs the Inputs / Styles tabs. Mount it with `key={drawing.id}`: the edits
 * start from the saved values.
 *
 * @param {object}   props
 * @param {object}   props.drawing - engine.getDrawings() item
 * @param {string[]} props.palette - engine.getLinePalette()
 * @param {(settings: { style: object, isVisible: boolean }) => void} props.onSave
 * @param {() => void} props.onRemove
 * @param {() => void} props.onClose
 */
const DrawingSettingsModal = ({ drawing, palette, onSave, onRemove, onClose }) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const { inputs, rows, rowsAfterLevels = NO_ROWS } = drawingSettingsOf(drawing.type);
  const allRows = [...rows, ...rowsAfterLevels];
  const [tab, setTab] = useState(TAB_INPUTS);
  const { draft, changeDraft, changeLevel, getSettings } = useDrawingDraft(drawing);

  const handlePlotChange = (key, change) =>
    changeDraft(rowChangeToDraft(allRows.find((row) => row.key === key), change));

  return (
    <ChartSettingsDialog
      title={t(drawingToolLabelId(drawing.type))}
      onSave={() => onSave(getSettings())}
      onRemove={onRemove}
      onClose={onClose}
    >
      {inputs && (
        <ChartTabs
          variant="pill"
          tabs={[
            { id: TAB_INPUTS, label: t("futures.chart.indicator_settings.tab_inputs") },
            { id: TAB_STYLES, label: t("futures.chart.indicator_settings.tab_styles") },
          ]}
          activeId={tab}
          onSelect={setTab}
        />
      )}
      {inputs && tab === TAB_INPUTS ? (
        <InputsTab inputs={inputs} values={draft} onChange={(key, value) => changeDraft({ [key]: value })} />
      ) : (
        <StylesTab
          plots={rows}
          plotStyles={rowsToPlotStyles(allRows, draft)}
          levels={draft.levels || NO_LEVELS}
          plotsAfterLevels={rowsAfterLevels}
          palette={palette}
          onPlotChange={handlePlotChange}
          onLevelChange={changeLevel}
        />
      )}
    </ChartSettingsDialog>
  );
};

export default DrawingSettingsModal;
